import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';
import { DetailItem } from 'app/shared/info-section/item';
import { GridColumn, GridRow } from 'app/layouts/grid';
import GmModal from 'app/shared/modal/modal';
import useValidator from 'hooks/validator';
import { useAgencyService } from 'hooks/iam/agency';
import { agencyValidationConfig } from 'app/shared/utils/form/validation/agency';
import { FlowShell } from '../components/flow-shell';
import { VerifiableField } from '../components/verifiable-field';
import { VerifyPhoneModal } from '../verify-phone-modal';
import { usePhoneVerifier } from '../hooks/use-phone-verifier';
import { useVerifier } from '../hooks/use-verifier';
import {
  BUSINESS_TYPES,
  COUNTRY_OPTIONS,
  PHONE_MAX_ATTEMPTS,
  BVN_MAX_ATTEMPTS,
  CAC_MAX_ATTEMPTS
} from '../data';

const STEPS = ['Business', 'Operator', 'Verification'];
const SUBTITLES = [
  'Tell us about your business and where to reach you.',
  'Tell us about the operator who manages this account.',
  'Verify your registered business and its operator.'
];

export const BusinessFlow = ({ onCreated, onBack }) => {
  const { email: user_email } = useSelector((state) => state.user_data);
  const existing_agency = useSelector((state) => state.metadata.agency);
  const { createAgency, updateAgency, managePhoneVerification, verifyBusiness, verifyBVN } =
    useAgencyService();
  const { errors, validateField } = useValidator(agencyValidationConfig);

  const [screen, setScreen] = useState(1);
  const [agency, setAgency] = useState(null);
  const [resumed, setResumed] = useState(false);
  const [selected_country, setSelectedCountry] = useState(null);
  const [selected_business_type, setSelectedBusinessType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form_data, setFormData] = useState({
    business_name: '',
    cac_number: '',
    tin: '',
    email: '',
    state: '',
    street: '',
    zip: '',
    firstname: '',
    lastname: '',
    operator_email: '',
    phone_number: '',
    bvn: ''
  });

  // Attempt counts persisted on the agency record, used to seed the verifiers
  // so the remaining-attempts indicator and lock state survive a reload.
  const attempts_seed = existing_agency?.verification_attempts || {};

  // All verifiers run after step 1, so the agency record (and its id) already
  // exists by the time any of these fire.
  const cac = useVerifier({
    maxAttempts: CAC_MAX_ATTEMPTS,
    initialAttempts: attempts_seed.cac || 0,
    verify: () =>
      verifyBusiness({
        data: {
          agency_id: agency?.id,
          cac_number: form_data.cac_number,
          business_name: form_data.business_name,
          business_type: selected_business_type?.value
        }
      })
  });
  const phone = usePhoneVerifier({
    maxAttempts: PHONE_MAX_ATTEMPTS,
    initialAttempts: attempts_seed.phone_number || 0,
    initiate: (value) =>
      managePhoneVerification('initiation', {
        data: { agency_id: agency?.id, phone_number: value }
      }),
    confirm: (value, code) =>
      managePhoneVerification('confirmation', { data: { phone_number: value, otp: code } })
  });
  const bvn = useVerifier({
    maxAttempts: BVN_MAX_ATTEMPTS,
    initialAttempts: attempts_seed.bvn || 0,
    verify: () => verifyBVN({ data: { agency_id: agency?.id, bvn: form_data.bvn } })
  });

  // Resume an unfinished business registration: pre-fill and skip the business basics.
  useEffect(() => {
    if (!existing_agency || !existing_agency.id) return;

    // Adopt the existing record so switching tracks updates it (flipping `track`)
    // instead of creating a duplicate. Only pre-fill when it's already a business.
    setAgency(existing_agency);
    if (existing_agency.track !== 'business') return;

    setResumed(true);
    const address = existing_agency.address || {};
    const status = existing_agency.verification_status || {};

    setFormData((prev) => ({
      ...prev,
      business_name: existing_agency.business_name || '',
      cac_number: existing_agency.cac_number || '',
      tin: existing_agency.tin || '',
      email: existing_agency.email || '',
      state: address.state || '',
      street: address.street || '',
      zip: address.zip || '',
      firstname: existing_agency.firstname || '',
      lastname: existing_agency.lastname || '',
      operator_email: existing_agency.operator_email || '',
      phone_number: existing_agency.phone_number || '',
      bvn: existing_agency.bvn || ''
    }));

    if (address.country) {
      const option = COUNTRY_OPTIONS.find(
        (o) => o.value === address.country || o.label === address.country
      );
      if (option) setSelectedCountry(option);
    }
    if (existing_agency.business_type) {
      const type = BUSINESS_TYPES.find((t) => t.value === existing_agency.business_type);
      if (type) setSelectedBusinessType(type);
    }

    if (status.cac && existing_agency.cac_number) cac.setVerifiedValue(existing_agency.cac_number);
    if (status.phone_number && existing_agency.phone_number)
      phone.setVerifiedValue(existing_agency.phone_number);
    if (status.bvn && existing_agency.bvn) bvn.setVerifiedValue(existing_agency.bvn);

    setScreen(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    validateField(field, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const cac_is_valid = /^[A-Za-z0-9-]{6,}$/.test(form_data.cac_number);
  const phone_is_valid = !!form_data.phone_number && !errors.phone_number;
  const bvn_is_valid = /^\d{11}$/.test(form_data.bvn);

  const cac_verified = cac.isVerified(form_data.cac_number);
  const phone_verified = phone.isVerified(form_data.phone_number);
  const bvn_verified = bvn.isVerified(form_data.bvn);

  // Step 1 — business identity, contact and address (creates the agency).
  const business_valid =
    !!form_data.business_name &&
    !errors.business_name &&
    !!selected_business_type &&
    !!form_data.email &&
    !errors.email &&
    !!selected_country &&
    !!form_data.state &&
    !!form_data.street &&
    !errors.street &&
    !!form_data.zip &&
    !errors.zip;

  // Step 2 — the operator who manages the account.
  const operator_valid =
    !!form_data.firstname &&
    !errors.firstname &&
    !!form_data.lastname &&
    !errors.lastname &&
    !!form_data.operator_email &&
    !errors.operator_email;

  // Step 3 — verify the registered business and its operator.
  const verification_valid = cac_verified && phone_verified && bvn_verified;

  // CAC verification returns the tax_id, which becomes the (read-only) TIN.
  const handleVerifyCAC = async () => {
    const details = await cac.run(form_data.cac_number);
    if (details && details.tin) {
      setFormData((prev) => ({ ...prev, tin: details.tin }));
    }
  };

  // Step 1 → 2: persist the business basics, creating the agency on first save.
  const saveBusiness = async () => {
    if (!business_valid) {
      if (resumed) setScreen(2);
      return;
    }

    const data = {
      track: 'business',
      business_name: form_data.business_name,
      business_type: selected_business_type.value,
      email: form_data.email,
      address: {
        country: selected_country.value,
        state: form_data.state,
        street: form_data.street,
        zip: form_data.zip
      },
      user_email
    };

    setSaving(true);
    if (agency) {
      const { error } = await updateAgency(agency.id, { data });
      setSaving(false);
      if (!error) setScreen(2);
      return;
    }

    const { agency: record } = await createAgency({ data });
    setSaving(false);
    if (record && record.id) {
      setAgency(record);
      setScreen(2);
    }
  };

  // Step 2 → 3: persist the operator's details before verification.
  const saveOperator = async () => {
    const data = {
      firstname: form_data.firstname,
      lastname: form_data.lastname,
      operator_email: form_data.operator_email
    };

    setSaving(true);
    const { error } = await updateAgency(agency.id, { data });
    setSaving(false);
    if (!error) setScreen(3);
  };

  // Step 3: persist the verified business + operator details and finish.
  const submitForm = async () => {
    const data = {
      cac_number: form_data.cac_number,
      tin: form_data.tin,
      phone_number: form_data.phone_number,
      bvn: form_data.bvn,
      registration_completed: true
    };

    setSaving(true);
    const { error } = await updateAgency(agency.id, { data });
    setSaving(false);
    if (!error) onCreated({ ...agency, ...data });
  };

  const handlePrimary = () => {
    if (screen === 1) return saveBusiness();
    if (screen === 2) return saveOperator();
    return submitForm();
  };

  const primary_disabled =
    screen === 1
      ? !(business_valid || resumed)
      : screen === 2
      ? !operator_valid
      : !verification_valid;

  const is_last = screen === STEPS.length;

  return (
    <FlowShell
      steps={STEPS}
      current={screen}
      title="Register your business"
      subtitle={SUBTITLES[screen - 1]}
      onBack={() => (screen === 1 ? onBack() : setScreen(screen - 1))}
      primaryLabel={is_last ? 'Create agency →' : 'Continue →'}
      onPrimary={handlePrimary}
      primaryDisabled={primary_disabled}
      busy={saving}
      modal={
        <GmModal
          show_title
          title="Verify your phone number"
          show_modal={phone.showModal}
          onClose={() => phone.setShowModal(false)}
        >
          <VerifyPhoneModal
            phone_number={form_data.phone_number}
            confirming={phone.confirming}
            resending={phone.requesting}
            attemptsLeft={phone.attemptsLeft}
            onConfirm={(code) => phone.submitCode(form_data.phone_number, code)}
            onResend={() => phone.request(form_data.phone_number)}
          />
        </GmModal>
      }
    >
      {screen === 1 && (
        <>
          <GridRow num_of_columns={2}>
            <GridColumn>
              <DetailItem dark title="Business Name" required>
                <IconInput
                  dark
                  input_id="business_name"
                  value={form_data.business_name}
                  onInput={(value) => handleChange('business_name', value)}
                  error={errors.business_name}
                />
              </DetailItem>
            </GridColumn>
            <GridColumn>
              <DetailItem dark title="Business Type" required>
                <MultiSelect
                  dark
                  input_id="business_type"
                  options={BUSINESS_TYPES}
                  value={selected_business_type}
                  onChange={setSelectedBusinessType}
                  isSorted={false}
                  placeholder="Select type"
                />
              </DetailItem>
            </GridColumn>
          </GridRow>

          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem dark title="Business Email" required>
                <IconInput
                  dark
                  type="email"
                  input_id="email"
                  value={form_data.email}
                  onInput={(value) => handleChange('email', value)}
                  error={errors.email}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>

          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem dark title="Business Address" required>
                <IconInput
                  dark
                  input_id="street"
                  value={form_data.street}
                  onInput={(value) => handleChange('street', value)}
                  error={errors.street}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>

          <GridRow num_of_columns={4}>
            <GridColumn span={2}>
              <DetailItem dark title="Country" required>
                <MultiSelect
                  dark
                  input_id="country"
                  options={COUNTRY_OPTIONS}
                  value={selected_country}
                  onChange={setSelectedCountry}
                  placeholder="Select country"
                />
              </DetailItem>
            </GridColumn>
            <GridColumn span={1}>
              <DetailItem dark title="State" required>
                <IconInput
                  dark
                  input_id="state"
                  value={form_data.state}
                  onInput={(value) => handleChange('state', value)}
                />
              </DetailItem>
            </GridColumn>
            <GridColumn span={1}>
              <DetailItem dark title="Zip" required>
                <IconInput
                  dark
                  input_id="zip"
                  value={form_data.zip}
                  onInput={(value) => handleChange('zip', value)}
                  error={errors.zip}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
        </>
      )}

      {screen === 2 && (
        <>
          <GridRow num_of_columns={2}>
            <GridColumn>
              <DetailItem dark title="First Name" required>
                <IconInput
                  dark
                  input_id="firstname"
                  value={form_data.firstname}
                  onInput={(value) => handleChange('firstname', value)}
                  error={errors.firstname}
                />
              </DetailItem>
            </GridColumn>
            <GridColumn>
              <DetailItem dark title="Last Name" required>
                <IconInput
                  dark
                  input_id="lastname"
                  value={form_data.lastname}
                  onInput={(value) => handleChange('lastname', value)}
                  error={errors.lastname}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem dark title="Email" required>
                <IconInput
                  dark
                  type="email"
                  input_id="operator_email"
                  value={form_data.operator_email}
                  onInput={(value) => handleChange('operator_email', value)}
                  error={errors.operator_email}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
        </>
      )}

      {screen === 3 && (
        <>
          <GridRow num_of_columns={2}>
            <GridColumn>
              <VerifiableField
                title="CAC Number"
                value={form_data.cac_number}
                onChange={(value) => handleChange('cac_number', value)}
                error={errors.cac_number}
                verified={cac_verified}
                verifying={cac.verifying}
                verifyLabel="Click to verify CAC"
                onVerify={handleVerifyCAC}
                showVerify={cac_is_valid}
                locked={cac.locked}
                attemptsLeft={cac.attemptsLeft}
              />
            </GridColumn>
            <GridColumn>
              <DetailItem dark title="TIN" required>
                <IconInput
                  dark
                  disabled
                  input_id="tin"
                  value={form_data.tin}
                  placeholder="Auto-filled from CAC check"
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={2}>
            <GridColumn>
              <VerifiableField
                title="Phone Number"
                value={form_data.phone_number}
                onChange={(value) => handleChange('phone_number', value)}
                error={errors.phone_number}
                verified={phone_verified}
                verifying={phone.requesting}
                verifyingText="Sending code…"
                verifyLabel="Click to verify phone number"
                onVerify={() => phone.request(form_data.phone_number)}
                showVerify={phone_is_valid}
                locked={phone.locked}
                attemptsLeft={phone.attemptsLeft}
              />
            </GridColumn>
            <GridColumn>
              <VerifiableField
                title="BVN"
                type="number"
                value={form_data.bvn}
                onChange={(value) => handleChange('bvn', value)}
                error={errors.bvn}
                verified={bvn_verified}
                verifying={bvn.verifying}
                verifyLabel="Click to verify BVN"
                onVerify={() => bvn.run(form_data.bvn)}
                showVerify={bvn_is_valid}
                locked={bvn.locked}
                attemptsLeft={bvn.attemptsLeft}
              />
            </GridColumn>
          </GridRow>
        </>
      )}
    </FlowShell>
  );
};
